import {
  FileText,
  Loader2,
  RotateCwSquare,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { motion } from "motion/react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { DeletePageDialog } from "~/features/chat/components/modals/DeletePageDialog";

type SelectedPagePanelProps = {
  isViewOnly: boolean;
  unselectPages: () => void;
  onRotateClicked: () => void;
  onDeleteClicked: () => void;
  onRecognizeClicked?: () => void;
  onZoomClicked?: () => void;
  selectedPageNumber?: number;
  isRotateLoading: boolean;
  isRecognizeLoading?: boolean;
  isZoomActive?: boolean;
};

export const SelectedPagePanel: FC<SelectedPagePanelProps> = ({
  isViewOnly,
  unselectPages,
  onRotateClicked,
  isRotateLoading,
  onDeleteClicked,
  onRecognizeClicked,
  onZoomClicked,
  selectedPageNumber,
  isRecognizeLoading = false,
  isZoomActive = false,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{
          y: 0,
          opacity: 1,
          transition: { duration: 0.2, ease: "easeOut" },
        }}
        exit={{
          y: 20,
          opacity: 0,
          transition: { duration: 0.15, ease: "easeIn" },
        }}
        className="h-14 px-4 py-2 rounded-xl shadow-[0_-4px_20px_rgba(59,130,246,0.3)] flex justify-center items-center bg-blue-600 text-white"
      >
        <div className="flex items-center gap-1">
          <div className="pr-2 flex items-center gap-5">
            <div className="text-white text-sm font-normal min-w-[100px]">
              {selectedPageNumber ? (
                <FormattedMessage
                  id="documentsList.pageSelected"
                  defaultMessage="Page {number} selected"
                  values={{
                    number: selectedPageNumber,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="documentsList.pageSelected"
                  defaultMessage="Page selected"
                />
              )}
            </div>
          </div>
          {onZoomClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-blue-600 hover:bg-blue-700 border-blue-500 !text-white pointer-events-auto"
                  onClick={onZoomClicked}
                >
                  {isZoomActive ? (
                    <ZoomOut className="w-4 h-4" />
                  ) : (
                    <ZoomIn className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.zoom" />
              </TooltipContent>
            </Tooltip>
          )}
          {onRecognizeClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-blue-600 hover:bg-blue-700 border-blue-500 !text-white pointer-events-auto"
                  onClick={onRecognizeClicked}
                  disabled={isRecognizeLoading}
                >
                  {isRecognizeLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.recognize" />
              </TooltipContent>
            </Tooltip>
          )}
          {!isViewOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-blue-600 hover:bg-blue-700 border-blue-500 !text-white pointer-events-auto"
                  onClick={onRotateClicked}
                  disabled={isRotateLoading || isZoomActive}
                >
                  {isRotateLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCwSquare className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.rotate" />
              </TooltipContent>
            </Tooltip>
          )}
          {!isViewOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-blue-600 hover:bg-blue-700 border-blue-500 !text-white pointer-events-auto"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isZoomActive}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.delete" />
              </TooltipContent>
            </Tooltip>
          )}
          <Button
            variant="outline"
            size="icon"
            className="bg-blue-600 hover:bg-blue-700 border-transparent !text-white pointer-events-auto"
            onClick={unselectPages}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
      <DeletePageDialog
        open={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onDelete={() => {
          onDeleteClicked();
          setIsDeleteDialogOpen(false);
        }}
        selectedCount={1}
      />
    </>
  );
};
